import { useEffect, useState } from 'react';

interface UseDelayedLoadingOptions {
	/**
	 * Delay in milliseconds before showing loading state
	 * @default 300
	 */
	delay?: number;
	/**
	 * Whether to show loading state immediately on initial load
	 * @default false
	 */
	showInitialLoading?: boolean;
}

/**
 * Hook that delays showing loading state to prevent flashing during quick data fetches
 *
 * @param isLoading - The actual loading state from data fetching
 * @param options - Configuration options
 * @returns Object with delayed loading state and utilities
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useQuery();
 * const { shouldShowLoading, hasLoadedOnce } = useDelayedLoading(isLoading, { delay: 300 });
 *
 * if (shouldShowLoading) {
 *   return <Skeleton />;
 * }
 * ```
 */
export function useDelayedLoading(
	isLoading: boolean,
	options: UseDelayedLoadingOptions = {}
) {
	const { delay = 300, showInitialLoading = false } = options;

	const [shouldShowLoading, setShouldShowLoading] =
		useState(showInitialLoading);
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	const [isFirstLoad, setIsFirstLoad] = useState(true);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout | null = null;

		if (isLoading) {
			// If this is the first load and showInitialLoading is true, show loading immediately
			if (isFirstLoad && showInitialLoading) {
				setShouldShowLoading(true);
			} else if (!isFirstLoad || showInitialLoading) {
				// For subsequent loads or when showInitialLoading is true, delay the loading state
				timeoutId = setTimeout(() => {
					setShouldShowLoading(true);
				}, delay);
			}
		} else {
			// Data has loaded
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			setShouldShowLoading(false);
			setHasLoadedOnce(true);
			setIsFirstLoad(false);
		}

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [isLoading, delay, showInitialLoading, isFirstLoad]);

	return {
		/** Whether to show loading state (delayed) */
		shouldShowLoading,
		/** Whether data has been loaded at least once */
		hasLoadedOnce,
		/** Whether this is the first load */
		isFirstLoad,
		/** The original loading state (immediate) */
		isLoading
	};
}
